import { useEffect, useMemo, useState, useCallback } from 'react'

const AGGREGATE_FNS = {
  sum: (values) => values.reduce((a, b) => a + b, 0),
  avg: (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0,
  min: (values) => values.length ? Math.min(...values) : 0,
  max: (values) => values.length ? Math.max(...values) : 0,
  count: (values) => values.length,
  first: (values) => values[0] ?? null,
  last: (values) => values[values.length - 1] ?? null
}

function buildCompositeKey (row, columns) {
  return columns.map(col => row[col] ?? '').join('\x00')
}

// O(leafCount) per call, called once per aggregate column per group node.
// Acceptable for typical datasets (<10k rows). For very large flat datasets
// with deep hierarchies, consider memoized leaf collection or bottom-up aggregation.
function collectLeafValues (node, column) {
  if (!node.subRows?.length) {
    const val = node[column]
    return val != null ? [Number(val)] : []
  }
  return node.subRows.flatMap(child => collectLeafValues(child, column))
}

function applyClientAggregation (node, columnAggregates) {
  if (!node.subRows?.length) return node

  const aggregatedChildren = node.subRows.map(child =>
    applyClientAggregation(child, columnAggregates)
  )

  const aggregatedNode = { ...node, subRows: aggregatedChildren }
  for (const { column, fn } of columnAggregates) {
    const aggFn = AGGREGATE_FNS[fn]
    if (!aggFn) continue
    const values = collectLeafValues(aggregatedNode, column)
    aggregatedNode[column] = aggFn(values)
  }
  return aggregatedNode
}

/**
 * Groups flat rows into a tree by composite-key grouping with mode: 'none' or 'client'.
 */
function buildTreeByGrouping (flatRows, levels, aggregation, labelColumn, leafLabelAccessor) {
  if (!flatRows?.length) return []

  function groupAtLevel (rows, levelIndex) {
    if (levelIndex >= levels.length) {
      return rows.map(row => {
        const leaf = { ...row, _isLeaf: true }
        if (labelColumn && leafLabelAccessor) {
          leaf[labelColumn] = row[leafLabelAccessor] ?? null
        }
        return leaf
      })
    }

    const level = levels[levelIndex]
    const { groupBy, labelAccessor } = level
    const groupMap = new Map()
    const groupOrder = []

    for (const row of rows) {
      const key = buildCompositeKey(row, groupBy)
      if (!groupMap.has(key)) {
        groupMap.set(key, [])
        groupOrder.push(key)
      }
      groupMap.get(key).push(row)
    }

    return groupOrder.map(key => {
      const groupRows = groupMap.get(key)
      const representative = groupRows[0]
      const subRows = groupAtLevel(groupRows, levelIndex + 1)

      const labelValue = representative[labelAccessor] ?? key
      const node = {
        _isGroupNode: true,
        _depth: levelIndex,
        _label: labelValue
      }

      if (labelColumn) {
        node[labelColumn] = labelValue
      }

      for (const col of groupBy) {
        node[col] = representative[col]
      }
      if (labelAccessor && !(labelAccessor in node)) {
        node[labelAccessor] = representative[labelAccessor]
      }

      node.subRows = subRows
      return node
    })
  }

  const tree = groupAtLevel(flatRows, 0)

  if (aggregation?.mode === 'client' && aggregation.columnAggregates?.length) {
    return tree.map(root => applyClientAggregation(root, aggregation.columnAggregates))
  }

  return tree
}

/**
 * Stitches pre-aggregated server rows by depth column and composite key prefix matching.
 */
function buildTreeByServerDepth (flatRows, levels, depthColumn, labelColumn, leafLabelAccessor) {
  if (!flatRows?.length || !depthColumn) return []

  const maxConfiguredDepth = levels.length
  const rowsByDepth = new Map()

  for (const row of flatRows) {
    const parsedDepth = Number(row[depthColumn])
    const depth = Number.isFinite(parsedDepth) ? parsedDepth : 0
    if (!rowsByDepth.has(depth)) rowsByDepth.set(depth, [])
    rowsByDepth.get(depth).push({ ...row, _depth: depth })
  }

  const depths = [...rowsByDepth.keys()].sort((a, b) => a - b)
  if (!depths.length) return []

  const rootDepth = depths[0]
  const roots = (rowsByDepth.get(rootDepth) || []).map(row => {
    const isGroup = rootDepth < maxConfiguredDepth
    const labelValue = row[levels[rootDepth]?.labelAccessor] ?? ''
    const node = {
      ...row,
      _isGroupNode: isGroup,
      _label: labelValue,
      subRows: []
    }
    if (labelColumn) {
      node[labelColumn] = isGroup ? labelValue : (leafLabelAccessor ? row[leafLabelAccessor] ?? null : null)
    }
    return node
  })

  for (let di = 1; di < depths.length; di++) {
    const depth = depths[di]
    const parentDepth = depths[di - 1]
    const childRows = rowsByDepth.get(depth) || []
    const level = levels[depth] || levels[levels.length - 1]
    const parentLevel = levels[parentDepth]

    if (!parentLevel) continue

    const parentGroupBy = parentLevel.groupBy

    const parentsByKey = new Map()
    const collectParents = (nodes) => {
      for (const node of nodes) {
        if (node._depth === parentDepth) {
          const key = buildCompositeKey(node, parentGroupBy)
          parentsByKey.set(key, node)
        }
        if (node.subRows?.length) collectParents(node.subRows)
      }
    }
    collectParents(roots)

    for (const childRow of childRows) {
      const parentKey = buildCompositeKey(childRow, parentGroupBy)
      const parent = parentsByKey.get(parentKey)

      const isGroup = depth < maxConfiguredDepth
      const isLeaf = depth >= maxConfiguredDepth
      const labelValue = level?.labelAccessor ? childRow[level.labelAccessor] ?? '' : ''

      const childNode = {
        ...childRow,
        _isGroupNode: isGroup,
        _isLeaf: isLeaf,
        _label: labelValue,
        subRows: []
      }

      if (labelColumn) {
        childNode[labelColumn] = isLeaf
          ? (leafLabelAccessor ? childRow[leafLabelAccessor] ?? null : null)
          : labelValue
      }

      if (parent) {
        parent.subRows.push(childNode)
      } else {
        roots.push(childNode)
      }
    }
  }

  return roots
}

function buildInitialExpandState (tree, defaultExpandDepth, currentDepth = 0) {
  const state = {}
  if (currentDepth >= defaultExpandDepth) return state

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.subRows?.length) {
      const rowId = String(i)
      state[rowId] = true
      const childState = buildInitialExpandState(node.subRows, defaultExpandDepth, currentDepth + 1)
      for (const [childKey, val] of Object.entries(childState)) {
        state[`${rowId}.${childKey}`] = val
      }
    }
  }
  return state
}

/**
 * Transforms flat row data into a nested tree using composite-key grouping.
 *
 * @param {Array<Object>} flatRows - flat row data from blueprint execution
 * @param {Object} hierarchyConfig - hierarchy configuration
 * @param {Array<{groupBy: string[], labelAccessor: string}>} hierarchyConfig.levels
 * @param {number} [hierarchyConfig.defaultExpandDepth=1]
 * @param {Object} [hierarchyConfig.aggregation]
 * @param {'none'|'client'|'server'} [hierarchyConfig.aggregation.mode='none']
 * @param {Array<{column: string, fn: string}>} [hierarchyConfig.aggregation.columnAggregates]
 * @param {string} [hierarchyConfig.aggregation.depthColumn]
 * @param {string} [hierarchyConfig.labelColumn='_hierarchyLabel'] - synthetic field written on every node
 * @param {string|null} [hierarchyConfig.leafLabelAccessor] - source column for leaf row labels
 */
export default function useBlueprintTreeData (flatRows, hierarchyConfig) {
  const {
    levels = [],
    defaultExpandDepth = 1,
    aggregation = { mode: 'none' },
    labelColumn = '_hierarchyLabel',
    leafLabelAccessor = null
  } = hierarchyConfig || {}

  const treeData = useMemo(() => {
    if (!flatRows?.length || !levels.length) return []

    if (aggregation.mode === 'server' && aggregation.depthColumn) {
      return buildTreeByServerDepth(flatRows, levels, aggregation.depthColumn, labelColumn, leafLabelAccessor)
    }

    return buildTreeByGrouping(flatRows, levels, aggregation, labelColumn, leafLabelAccessor)
  }, [flatRows, levels, aggregation, labelColumn, leafLabelAccessor])

  const initialExpandState = useMemo(() => {
    return buildInitialExpandState(treeData, defaultExpandDepth)
  }, [treeData, defaultExpandDepth])

  const [expandState, setExpandState] = useState(initialExpandState)

  useEffect(() => {
    setExpandState(initialExpandState)
  }, [initialExpandState])

  const resetExpandState = useCallback(() => {
    setExpandState(initialExpandState)
  }, [initialExpandState])

  return {
    treeData,
    expandState,
    setExpandState,
    resetExpandState
  }
}
